// TIP-609 — the status modal opened from a table chip. 460px, its three steps
// grouped in a panel, closing on the backdrop or the cross, as designed.
// TIP-610 adds what GET /api/briefs/orders/<reference>/ returns: the source
// bucket, which rewrites the first tracker line, and a short recap of what the
// customer asked for. The modal renders from the ROW first and fills the rest
// in when the detail lands, so opening it is never a wait.
import React, { useEffect } from "react";
import "./counterpartyBriefs.css";
import { BRIEF_STATUSES, STATUS_MODAL_COPY } from "./briefsFixtures";

const Step = ({ state, mark, label, detail, meta }) => (
  <div className={`cb-step cb-step--${state}`}>
    <span className="cb-step-mark">{mark}</span>
    <span className="cb-step-text">
      <span className="cb-step-label">{label}</span>
      <span className="cb-step-detail">{detail}</span>
    </span>
    {meta && <span className="cb-step-meta">{meta}</span>}
  </div>
);

const Field = ({ label, value }) =>
  value ? (
    <div className="cb-detail-field">
      <span className="cb-detail-label">{label}</span>
      <span className="cb-detail-value">{value}</span>
    </div>
  ) : null;

const StatusModal = ({ brief, onClose }) => {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!brief) return null;
  const copy = STATUS_MODAL_COPY[brief.status];
  if (!copy) return null;

  const isSnag = brief.status === BRIEF_STATUSES.SNAG;
  const isBuilding = brief.status === BRIEF_STATUSES.BUILDING;
  const s = STATUS_MODAL_COPY.steps;
  const d = STATUS_MODAL_COPY.detail;
  const detail = brief.detail || null;

  // The first step's wording is a FACT about which bucket paid, not a label.
  // Without the detail it stays the allowance wording, which is what all but
  // the bought orders are.
  const stepOneLabel = detail?.sourceBucket
    ? STATUS_MODAL_COPY.source[detail.sourceBucket] || s.one.label
    : s.one.label;

  return (
    <div className="cb-modal-backdrop" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={copy.heading}
        data-testid="cb-status-modal"
        className="cb-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="cb-modal-head">
          <div>
            <p className="cb-modal-eyebrow">{STATUS_MODAL_COPY.eyebrow}</p>
            <h4 className="cb-modal-title">{copy.heading}</h4>
          </div>
          <button type="button" className="cb-modal-close" onClick={onClose} aria-label="Close">
            &times;
          </button>
        </div>

        <div className="cb-modal-lede">
          <p>{copy.body(brief.company)}</p>
        </div>

        <div className="cb-modal-foot">
          {/* The analyst's own words. The design shows the customer nothing. */}
          {isSnag && (
            <div data-testid="cb-snag-reason" className="cb-snag-reason">
              {brief.snagReason || STATUS_MODAL_COPY.defaultSnagReason}
            </div>
          )}

          <div className="cb-steps">
            <Step state="done" mark="&#10003;" label={stepOneLabel} detail={s.one.detail} meta={brief.ordered} />
            <Step
              state={isSnag ? "done" : isBuilding ? "now" : "pending"}
              mark={isBuilding ? "●" : "2"}
              label={s.two.label}
              detail={s.two.detail}
              meta={copy.step2Meta}
            />
            <Step
              state={isSnag ? "failed" : "pending"}
              mark={isSnag ? "×" : "3"}
              label={s.three.label}
              detail={s.three.detail}
              meta={isSnag ? null : s.three.meta}
            />
          </div>

          {/* Everything below is the customer's own submission read back to
              them. `notes` is the note they wrote TO the analyst — never the
              analyst's answer, and never a snag reason. */}
          {brief.detailLoading && (
            <p role="status" className="cb-detail-loading">{d.loading}</p>
          )}

          {detail && (detail.company || detail.purpose || detail.requesterName || detail.notes) && (
            <div data-testid="cb-brief-detail" className="cb-detail">
              <p className="cb-detail-heading">{d.heading}</p>
              <Field label={d.company} value={detail.country ? `${detail.company}, ${detail.country}` : detail.company} />
              <Field label={d.purpose} value={detail.purpose} />
              <Field
                label={d.requester}
                value={
                  detail.requesterName && detail.requesterEmail
                    ? `${detail.requesterName}, ${detail.requesterEmail}`
                    : detail.requesterName || detail.requesterEmail
                }
              />
              <Field label={d.notes} value={detail.notes} />
            </div>
          )}

          <p className="cb-modal-note">{STATUS_MODAL_COPY.footnote}</p>
        </div>
      </div>
    </div>
  );
};

export default StatusModal;
